/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoiceGuideFileBrowser.tsx
 🗓️ Created: 2026-03-24
 🧩 Description:
    Audio file browser for an existing Voice Guide.
    Lists all generated .wav files with inline HTML5 playback,
    real-time search, and metadata display.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback, useId } from 'react';
import {
  Search,
  FolderOpen,
  Play,
  Pause,
  Loader2,
  RefreshCw,
  Volume2,
  AlertCircle,
} from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../hooks/useI18n';
import { fetchVoiceGuideFiles, getVoiceGuideStreamUrl, VoiceGuideAudioFile } from '../api/voiceGuidesApi';

interface VoiceGuideFileBrowserProps {
  guideId: number;
  /** Base sound path prefix (e.g. "en/us/emma/") — used to build stream URLs */
  soundPath: string;
  onSelectFile?: (file: VoiceGuideAudioFile) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatRelativePath(p: string): string {
  return p.replace(/^\//, '');
}

/**
 * Inline mini audio player for a single WAV file.
 *
 * Strategy:
 *  1. On first play: fetch the WAV with auth headers → Blob → Object URL.
 *  2. Set audioRef.src + call load() → wait for 'canplaythrough'.
 *  3. Call play().
 *  4. On subsequent plays of the same file: reuse the existing blob URL.
 */
function AudioPlayer({
  streamUrl,
  label,
  onPlay,
  isActive,
}: {
  streamUrl: string;
  label: string;
  onPlay: () => void;
  isActive: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);   // persists across renders without triggering re-render
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const id = useId();

  // Stop when another row becomes active
  useEffect(() => {
    if (!isActive && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
  }, [isActive]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const handleToggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    setAudioError(null);

    // ── PAUSE ─────────────────────────────────────────────────
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }

    // Notify parent (stops other players)
    onPlay();

    // ── RESUME (blob already loaded) ───────────────────────────
    if (blobUrlRef.current) {
      try {
        audio.currentTime = 0;
        await audio.play();
        setPlaying(true);
      } catch (err) {
        console.error('[AudioPlayer] play() failed:', err);
        setAudioError('Playback failed');
      }
      return;
    }

    // ── FIRST PLAY: fetch → blob → load → play ─────────────────
    setLoading(true);
    try {
      const { getApiHeaders } = await import('../../../config/api');

      console.debug('[AudioPlayer] Fetching:', streamUrl);
      const response = await fetch(streamUrl, { headers: getApiHeaders() });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.debug('[AudioPlayer] Blob received, size:', blob.size, 'type:', blob.type);

      if (blob.size === 0) throw new Error('Audio file is empty');

      // Create object URL and assign to the audio element
      const objectUrl = URL.createObjectURL(blob);
      blobUrlRef.current = objectUrl;

      audio.src = objectUrl;
      audio.load();  // ← CRITICAL: tells the browser to reload with the new src

      // Wait until the audio is ready to play
      await new Promise<void>((resolve, reject) => {
        const onReady = () => {
          audio.removeEventListener('canplaythrough', onReady);
          audio.removeEventListener('error', onError);
          resolve();
        };
        const onError = () => {
          audio.removeEventListener('canplaythrough', onReady);
          audio.removeEventListener('error', onError);
          reject(new Error(`Audio decode error (code: ${audio.error?.code})`));
        };
        audio.addEventListener('canplaythrough', onReady, { once: true });
        audio.addEventListener('error', onError, { once: true });

        // Safety timeout — some browsers never fire canplaythrough for 8kHz WAV
        setTimeout(() => {
          audio.removeEventListener('canplaythrough', onReady);
          audio.removeEventListener('error', onError);
          resolve();  // try to play anyway
        }, 3000);
      });

      await audio.play();
      setPlaying(true);
    } catch (err) {
      console.error('[AudioPlayer] Failed to load/play audio:', err);
      setAudioError(err instanceof Error ? err.message : 'Playback failed');
      // Cleanup failed blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="flex items-center gap-1" title={audioError ?? undefined}>
      <audio
        ref={audioRef}
        id={id}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onError={(e) => {
          const el = e.currentTarget;
          console.error('[AudioPlayer] Audio element error:', el.error);
          setPlaying(false);
          setAudioError(`Decode error (code: ${el.error?.code ?? '?'})`);
        }}
        preload="none"
        className="hidden"
        aria-label={label}
      />
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        aria-label={playing ? 'Pause' : 'Play'}
        className={`flex items-center justify-center h-7 w-7 rounded-full transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60
          ${audioError
            ? 'bg-destructive/10 text-destructive'
            : playing
              ? 'bg-primary text-primary-foreground shadow-md'
              : 'bg-primary/10 hover:bg-primary/20 text-primary'
          }
          disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : audioError ? (
          <AlertCircle className="h-3.5 w-3.5" />
        ) : playing ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5 ml-0.5" />
        )}
      </button>
    </span>
  );
}


/**
 * File browser panel for an existing voice guide.
 * Shows all .wav files with search, play controls, size, and date.
 */
export function VoiceGuideFileBrowser({ guideId, soundPath, onSelectFile }: VoiceGuideFileBrowserProps) {
  const { t } = useI18n();
  const [files, setFiles] = useState<VoiceGuideAudioFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadFiles = useCallback(async (searchTerm: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVoiceGuideFiles(guideId, searchTerm || undefined);
      setFiles(result.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [guideId]);

  // Initial load
  useEffect(() => {
    loadFiles('');
  }, [loadFiles]);

  // Debounced search
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadFiles(value), 350);
  }, [loadFiles]);

  const handleRefresh = useCallback(() => {
    loadFiles(search);
  }, [loadFiles, search]);

  // Build absolute-ish stream URL for a file entry.
  // sound_path may be "en/us/emma/" or "/usr/share/.../en/us/emma/"
  const buildStreamUrl = (file: VoiceGuideAudioFile) => {
    const base = soundPath.endsWith('/') ? soundPath : soundPath + '/';
    const combined = base.startsWith('/')
      ? base + file.relativePath          // already absolute → pass as-is
      : base + file.relativePath;         // relative → backend will prepend SOUNDS_BASE_DIR
    return getVoiceGuideStreamUrl(combined);
  };

  return (
    <FormBox
      title={t('voiceGuides:browser.title') || 'Audio Files'}
      icon={<FolderOpen className="h-4 w-4" />}
      headerContent={
        <div className="flex items-center gap-3">
          {/* File count badge */}
          <span className="text-xs text-muted-foreground">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
            ) : (
              `${files.length} ${t('common:files') || 'files'}`
            )}
          </span>

          {/* Refresh button */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="p-1 rounded hover:bg-muted/60 transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
            title={t('common:refresh') || 'Refresh'}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      }
    >
      {/* Search bar */}
      <div className="mb-3 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('voiceGuides:browser.searchPlaceholder') || 'Search audio files...'}
          className="pl-9 h-8 text-sm"
        />
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm mb-3">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* File table */}
      {!error && (
        <div className="border rounded-md overflow-hidden">
          <div className="max-h-72 overflow-y-auto">
            {loading && files.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-muted-foreground gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('common:loading') || 'Loading...'}
              </div>
            ) : files.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
                <Volume2 className="h-8 w-8 opacity-30" />
                <p className="text-sm">
                  {search
                    ? (t('voiceGuides:browser.noResults') || 'No files match your search.')
                    : (t('voiceGuides:browser.empty') || 'No audio files generated yet.')}
                </p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-card text-xs sticky top-0 z-10 border-b border-border">
                  <tr>
                    <th className="text-center px-2 py-2 font-medium w-10">
                      {/* Play */}
                    </th>
                    <th className="text-left px-3 py-2 font-medium">
                      {t('voiceGuides:browser.file') || 'File'}
                    </th>
                    <th className="text-right px-3 py-2 font-medium w-20">
                      {t('voiceGuides:browser.size') || 'Size'}
                    </th>
                    <th className="text-right px-3 py-2 font-medium w-32 hidden sm:table-cell">
                      {t('voiceGuides:browser.modified') || 'Modified'}
                    </th>
                    {onSelectFile && (
                      <th className="text-center px-3 py-2 font-medium w-16">
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {files.map((file) => {
                    const streamUrl = buildStreamUrl(file);
                    const isActive = activeFile === file.relativePath;

                    return (
                      <tr
                        key={file.relativePath}
                        className={`hover:bg-muted/30 transition-colors ${isActive ? 'bg-primary/5' : ''}`}
                      >
                        {/* Play button */}
                        <td className="px-2 py-1.5 text-center">
                          <AudioPlayer
                            streamUrl={streamUrl}
                            label={file.relativePath}
                            isActive={isActive}
                            onPlay={() => setActiveFile(file.relativePath)}
                          />
                        </td>

                        {/* File path */}
                        <td className="px-3 py-1.5">
                          <span className="font-mono text-xs text-primary break-all leading-relaxed">
                            {formatRelativePath(file.relativePath)}
                          </span>
                        </td>

                        {/* Size */}
                        <td className="px-3 py-1.5 text-right">
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {formatFileSize(file.size)}
                          </span>
                        </td>

                        {/* Modified date */}
                        <td className="px-3 py-1.5 text-right hidden sm:table-cell">
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {new Date(file.modifiedAt).toLocaleDateString()}
                          </span>
                        </td>

                        {/* Select Action */}
                        {onSelectFile && (
                          <td className="px-2 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={() => onSelectFile(file)}
                              className="text-primary hover:bg-primary/10 p-1 rounded transition-colors text-xs font-medium"
                            >
                              {t('common:select') || 'Select'}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer: sound_path info */}
          {soundPath && (
            <div className="bg-muted/30 px-3 py-2 border-t flex items-center gap-2">
              <FolderOpen className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <code className="text-xs text-muted-foreground truncate">{soundPath}</code>
            </div>
          )}
        </div>
      )}
    </FormBox>
  );
}
